import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const CustomerDisplay = () => {
  const [orders, setOrders] = useState([]);
  const [websocketConnected, setWebsocketConnected] = useState(false);
  const websocketRef = useRef(null);
  const reconnectTimeout = useRef(null);

  const location = useLocation();
  const locationID = new URLSearchParams(location.search).get('locationID');
  const wsUrl = `${process.env.REACT_APP_WS}?pid=${locationID}`;
  const token = sessionStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      console.error('No token found in session storage');
      return;
    }

    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_SRV}/orders/pending`, {
          headers: { 
              Authorization: `Bearer ${token}`,
              lid: locationID
          }
        });
    
        if (response.data.success && Array.isArray(response.data.orders)) {
          setOrders(response.data.orders);
        } else {
          console.error('Invalid response format:', response.data);
        }
      } catch (error) {
        console.error('Error fetching orders:', error.response?.data || error.message);
      }
    };

    fetchOrders();

    const connectWebSocket = () => {
      if (websocketRef.current && websocketRef.current.readyState !== WebSocket.CLOSED) {
        console.log('WebSocket is already connected or connecting.');
        return;
      }

      const ws = new WebSocket(wsUrl, ['token', token]);
      websocketRef.current = ws;

      ws.onopen = () => {
        console.log('CustomerDisplay WebSocket connected');
        setWebsocketConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const updatedOrder = JSON.parse(event.data);
          console.log('Received WebSocket message:', updatedOrder);

          setOrders((prevOrders) => {
            const orderExists = prevOrders.some(order => order._id === updatedOrder._id);

            if (updatedOrder.status === 'served' || updatedOrder.status === 'dismissed') {
              return prevOrders.filter(order => order._id !== updatedOrder._id);
            } else if (orderExists) {
              return prevOrders.map(order => order._id === updatedOrder._id ? updatedOrder : order);
            } else {
              return [...prevOrders, updatedOrder];
            }
          });
        } catch (error) {
          console.error('Error processing incoming WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('CustomerDisplay WebSocket error:', error);
      };

      ws.onclose = (event) => {
        console.log('CustomerDisplay WebSocket closed', event);
        setWebsocketConnected(false);
        if (!reconnectTimeout.current) {
          reconnectTimeout.current = setTimeout(connectWebSocket, 5000); // Reconnect after 5 seconds
        }
      };
    };

    connectWebSocket();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && (!websocketRef.current || websocketRef.current.readyState === WebSocket.CLOSED)) {
        connectWebSocket();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (websocketRef.current) {
        websocketRef.current.close();
      }
      clearTimeout(reconnectTimeout.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [wsUrl, token, locationID]);

  const pendingOrders = orders.filter(order => order.status === 'pending');
  const readyOrders = orders.filter(order => order.status === 'ready');

  return (
    <div style={styles.container}>
      <div style={{ ...styles.connectionIndicator, backgroundColor: websocketConnected ? 'green' : 'red' }} />

      <div style={styles.column}>
        <h2 style={styles.header}>PENDING</h2>
        <div style={styles.ordersContainer}>
          {pendingOrders.map(order => (
            <div key={order._id} style={styles.pendingOrderBox}>
              {String(order.ticket).padStart(4, '0')}
            </div>
          ))}
        </div>
      </div>

      <div style={styles.column}>
        <h2 style={styles.header}>READY</h2>
        <div style={styles.ordersContainer}>
          {readyOrders.map(order => (
            <div key={order._id} style={styles.readyOrderBox}>
              {String(order.ticket).padStart(4, '0')}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    height: '100vh',
    backgroundColor: '#f9f9f9',
    padding: '20px',
    gap: '20px',
    position: 'relative',
  },
  column: {
    width: '50%',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    padding: '20px',
    overflowY: 'auto',
  },
  ordersContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
  },
  header: {
    textAlign: 'center',
    fontSize: '4rem',
    fontWeight: 'bold',
    marginBottom: '20px',
    borderBottom: '2px solid #ddd',
    paddingBottom: '10px',
    color: '#333',
  },
  pendingOrderBox: {
    width: '18rem',
    margin: '0.5rem',
    backgroundColor: '#f1c40f',
    color: '#fff',
    padding: '10px 0',
    height: '150px',
    fontSize: '7rem',
    fontWeight: 'bold',
    textAlign: 'center',
    borderRadius: '8px',
    boxShadow: '0px 14px 14px rgba(0, 0, 0, 0.4)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  readyOrderBox: {
    width: '18rem',
    margin: '0.5rem',
    backgroundColor: '#2ecc71',
    color: '#fff',
    padding: '10px 0',
    height: '150px',
    fontSize: '7rem',
    fontWeight: 'bold',
    textAlign: 'center',
    borderRadius: '8px',
    boxShadow: '0px 14px 14px rgba(0, 0, 0, 0.4)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  connectionIndicator: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.2)',
  },
};

export default CustomerDisplay;
